import { UserType } from '@inplay/models';
import { FC, MouseEvent, RefObject, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useOrganizationUnitData from '../../hooks/useOrganizationUnitData';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import { route } from '../../service/router';
import {
  checkVenueSetupNeeded,
  validateAndDecodeAuthToken,
} from '../../utils/helper';
import { ErrorMessages, StorageKey } from '../../utils/helper.type';
import { OnboardingStep, UnifiedOnboardingProps } from './onboarding.type';
import {
  getOrganizationUnitLabel,
  renderUnifiedView,
} from './onboardingHelper';
import { useLogoutData } from '../../hooks/useAuthData';
import { useTranslation } from 'react-i18next';
import { UserService } from '../../service/userService';
import { toast } from 'react-toastify';
import { useUserDetailContext } from '../../service/context/userDetailContext';
import SignatureCanvas from 'react-signature-canvas';
import ReactSignatureCanvas from 'react-signature-canvas';
import { useOnboarding } from '../../hooks/useOnboarding';

const Onboarding: FC<UnifiedOnboardingProps> = ({ initialStep = 1 }) => {
  const navigate = useNavigate();
  const { isDesktop } = useResponsiveLayout();
  const organizationUnit = useOrganizationUnitData();
  const tokenData = validateAndDecodeAuthToken();
  const [currentStep, setCurrentStep] = useState<OnboardingStep>(initialStep);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [photoReview, setPhotoReview] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<Record<string, string>>(
    {},
  );
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const [isDrawing, setIsDrawing] = useState<boolean>(false);

  const [isSigned, setIsSigned] = useState<boolean>(false);
  const sigCanvas: RefObject<SignatureCanvas> = useRef<SignatureCanvas>(null);
  const { logout, logoutLoading } = useLogoutData();
  const { t } = useTranslation();
  const userService = new UserService();
  const staffPin = localStorage.getItem(StorageKey.STAFF_PIN);
  const fileInputRef: RefObject<HTMLInputElement> =
    useRef<HTMLInputElement | null>(null);
  const { updateUserDetail } = useUserDetailContext();

  const { formData, handleChange } = useOnboarding();

  // Convert file to base64
  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });
  };

  // Handle file selection for step 2
  const handleFileSelect = async (file: File) => {
    const url = URL.createObjectURL(file);

    try {
      const base64 = await convertFileToBase64(file);
      handleChange('profilePicture', file);
      handleChange('profilePictureUrl', url);
      handleChange('profilePictureBase64', base64);
      handleChange('fileName', file.name);
    } catch (error) {
      console.error('Error converting file to base64:', error);
      // Still set the file data even if base64 conversion fails
      handleChange('profilePicture', file);
      handleChange('profilePictureUrl', url);
      handleChange('profilePictureBase64', null);
      handleChange('fileName', file.name);
    }
  };

  // Handle file deselection for step 2
  const handleFileDeselect = () => {
    if (formData.profilePictureUrl) {
      URL.revokeObjectURL(formData.profilePictureUrl);
    }
    handleChange('profilePicture', null);
    handleChange('profilePictureUrl', null);
    handleChange('profilePictureBase64', null);
    handleChange('fileName', null);
  };

  // Validate current step
  const validateCurrentStep = (): boolean => {
    const errors: Record<string, string> = {};

    if (currentStep === 1) {
      if (!formData?.firstName?.trim()) {
        errors.firstName =
          ErrorMessages.FIRST_NAME_REQUIRED || 'First name is required';
      }
      if (!formData?.lastName?.trim()) {
        errors.lastName =
          ErrorMessages.LAST_NAME_REQUIRED || 'Last name is required';
      }
      if (!tokenData?.type) {
        errors.userType =
          ErrorMessages.USER_TYPE_REQUIRED || 'User type is required';
      }
      if (!organizationUnit) {
        const fieldLabel = getOrganizationUnitLabel(
          tokenData?.type as UserType,
        );
        errors.organizationUnit = `${fieldLabel} is required`;
      }
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  useEffect(() => {
    if (currentStep === 3 && formData.signatureBase64) {
      setImagePreview(formData.signatureBase64);
      setIsDrawing(false);
      setIsSigned(true);
    }
  }, [currentStep]);

  // Handle continue to next step
  const handleContinue = async () => {
    if (currentStep === 1) {
      if (!validateCurrentStep()) return;
      setCurrentStep(2);
    } else if (currentStep === 2) {
      // Profile picture is optional, continue regardless
      setCurrentStep(3);
    } else if (currentStep === 3) {
      const canvas: ReactSignatureCanvas | null = sigCanvas.current;

      if (canvas && !canvas.isEmpty()) {
        const dataUrl: string = canvas
          .getTrimmedCanvas()
          .toDataURL('image/png');
        handleChange('signatureBase64', dataUrl);
        setImagePreview(dataUrl);
        setCurrentStep(4);
      } else if (formData.signatureBase64) {
        setCurrentStep(4);
      }
    } else if (currentStep === 4) {
      setIsLoading(true);
      try {
        // Submit all data to complete onboarding
        await userService.completeOnboarding({
          firstName: formData.firstName,
          lastName: formData.lastName,
          profilePhoto: formData.profilePictureBase64 || '',
          signature: formData.signatureBase64 ?? '',
          pin:
            (tokenData?.type === UserType.VENUE_STAFF ||
              tokenData?.type === UserType.VENUE_BRANCH_LOGIN) &&
            staffPin
              ? staffPin
              : undefined,
        });

        // Update user details in context with modified fields
        updateUserDetail({
          firstName: formData.firstName,
          lastName: formData.lastName,
          profilePhoto: formData.profilePictureBase64 || '',
        });

        // Clear data and navigate to dashboard
        localStorage.removeItem('onboardingData');

        // Check if user is venue admin/manager and if venue setup is needed
        const venueUnitDetails = JSON.parse(
          localStorage.getItem(StorageKey.VENUE_UNIT_DETAILS) || '{}',
        );
        if (
          (tokenData?.type === UserType.VENUE_ADMIN ||
            tokenData?.type === UserType.VENUE_MANAGER) &&
          checkVenueSetupNeeded(venueUnitDetails)
        ) {
          navigate(route.venueSetup);
        } else {
          navigate(route.dashboard);
        }
      } catch (error) {
        console.error('Error finishing onboarding:', error);
        toast.error(t(`[error] ${error}`));
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Handle skip for step 2
  const handleSkip = () => {
    if (currentStep === 2) {
      // Clear profile picture data when skipping step 2
      if (formData.profilePictureUrl) {
        URL.revokeObjectURL(formData.profilePictureUrl);
      }
      handleChange('profilePicture', null);
      handleChange('profilePictureUrl', null);
      handleChange('profilePictureBase64', null);
      handleChange('fileName', null);
      setCurrentStep(3);
    }
    if (currentStep === 3) {
      setCurrentStep(4);
    }
  };

  // Handle back navigation
  const handleBack = async (): Promise<void> => {
    console.log(`[ENTRY] [onboarding] (handleBack)...`);

    if (currentStep === 1) {
      try {
        console.log(`(handleBack) Before logging out....`);
        await logout(formData.email || '');
        console.log(`(handleBack) After Logging out.....`);
      } catch (error: unknown) {
        console.error('Error during logout:', error);
        throw error;
      }
    } else if (currentStep > 1) {
      console.log(
        `(handleBack) Navigating to previous step: ${currentStep - 1}`,
      );
      setCurrentStep((currentStep - 1) as OnboardingStep);
    } else {
      const incorrectStepError = new Error(
        `(handleBack) Incorrect step: ${currentStep}`,
      );
      console.warn(incorrectStepError);
      throw incorrectStepError;
    }
  };

  const handleTakePhoto = () => {
    setShowCamera(true);
  };

  const handlePhotoCapture = (imageSrc: string) => {
    // Convert base64 to File object
    fetch(imageSrc)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], 'camera-photo.jpg', {
          type: 'image/jpeg',
        });
        handleFileSelect(file);
        setPhotoReview(true);
        setShowCamera(false);
      });
  };

  const handleRetake = () => {
    handleFileDeselect();
    setPhotoReview(false);
    setShowCamera(true);
  };

  const handleCloseButton = () => {
    handleFileDeselect();
    setShowCamera(false);
    setPhotoReview(false);
  };

  const handleGalleryUpload = (file: File) => {
    handleFileSelect(file);
  };

  const handleSignatureDrawingClick = (e: MouseEvent<HTMLSpanElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('label')) return;

    setIsDrawing(true);
  };

  const handleUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (file && file.type.startsWith('image/')) {
      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
      setIsDrawing(false);

      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result as string;
        handleChange('signatureBase64', base64);
      };

      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
      handleChange('signatureBase64', '');
    }
  };
  const handleDelete = () => {
    if (imagePreview) {
      setImagePreview(null);
    } else {
      sigCanvas.current?.clear();
      setIsDrawing(false);
      setIsSigned(false);
    }
  };

  const handleSignBegin = () => {
    setIsSigned(true);
  };

  return (
    <div className="relative min-h-screen w-full">
      {renderUnifiedView({
        organizationUnit,
        currentStep,
        profileData: formData,
        handleChange,
        handleFileSelect: handleGalleryUpload,
        handleFileDeselect,
        handleContinue,
        handleSkip,
        handleBack,
        isLoading: logoutLoading || isLoading,
        errorMessages,
        isDesktop,
        userType: tokenData?.type || '',
        t,
        showCamera,
        photoReview,
        handleTakePhoto,
        handlePhotoCapture,
        handleRetake,
        handleCloseButton,
        handleSignatureDrawingClick,
        handleUpload,
        isDrawing,
        sigCanvas,
        handleDelete,
        isSigned,
        handleSignBegin,
        imagePreview,
        fileInputRef,
      })}
    </div>
  );
};

export default Onboarding;
